/**
 * EngageSMS — /engage/sms
 * SMS Campaign composer + campaign history list.
 */
import React, { useState } from 'react';
import { MessageSquare, Send, Loader2, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAudiences }          from '../context/AudienceContext';
import { useCampaigns }          from '../context/CampaignsContext';
import { useRole }               from '../context/RoleContext';
import CampaignComposerModal     from '../components/engage/CampaignComposerModal';
import SMSCampaignHistoryCard    from '../components/engage/SMSCampaignHistoryCard';

const EngageSMS = () => {
  const { audiences } = useAudiences();
  const { campaigns, loading } = useCampaigns();
  const { role } = useRole();
  const [composerOpen, setComposerOpen] = useState(false);

  const canSend = ['owner', 'admin', 'sales'].includes(role);

  // Quick stats
  const sent       = campaigns.filter(c => c.status === 'sent').length;
  const scheduled  = campaigns.filter(c => c.status === 'scheduled').length;
  const inProgress = campaigns.filter(c => c.status === 'sending').length;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">SMS Campaigns</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Send targeted SMS messages to your saved audiences
          </p>
        </div>
        {canSend && (
          <Button
            onClick={() => setComposerOpen(true)}
            disabled={audiences.length === 0}
            title={audiences.length === 0 ? 'Create an audience first' : undefined}
            className="text-white shrink-0"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="new-campaign-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        )}
      </div>

      {/* Quick stats row */}
      {campaigns.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <StatChip icon={CheckCircle2} color="text-green-600 bg-green-50" label="Sent"       value={sent} />
          <StatChip icon={Clock}        color="text-amber-600 bg-amber-50"  label="Scheduled"  value={scheduled} />
          <StatChip icon={MessageSquare} color="text-blue-600 bg-blue-50"  label="In Progress" value={inProgress} />
        </div>
      )}

      {/* Campaign list */}
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <MessageSquare className="w-7 h-7 text-gray-300" />
          </div>
          <h3 className="text-base font-semibold text-gray-800 mb-1">No campaigns yet</h3>
          <p className="text-sm text-gray-500 mb-5 max-w-sm mx-auto leading-relaxed">
            Create an audience first, then send a targeted SMS.
            Opted-out contacts are excluded automatically.
          </p>
          {canSend && audiences.length > 0 && (
            <Button
              onClick={() => setComposerOpen(true)}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="create-campaign-empty-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Your First Campaign
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="sms-campaign-list">
          {campaigns.map((c) => (
            <SMSCampaignHistoryCard key={c.id} campaign={c} userRole={role} />
          ))}
        </div>
      )}

      <CampaignComposerModal
        isOpen={composerOpen}
        onClose={() => setComposerOpen(false)}
      />
    </div>
  );
};

const StatChip = ({ icon: Icon, color, label, value }) => (
  <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border border-gray-200 bg-white`}>
    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${color.split(' ')[1]}`}>
      <Icon className={`w-3.5 h-3.5 ${color.split(' ')[0]}`} />
    </div>
    <div>
      <p className="text-lg font-bold text-gray-900 leading-none">{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
    </div>
  </div>
);

export default EngageSMS;
